import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { Registry } from 'vscode/vscode/vs/platform/registry/common/platform';
import { Extensions } from 'vscode/vscode/vs/platform/jsonschemas/common/jsonContributionRegistry';
import { fontIdRegex, fontWeightRegex, fontStyleRegex, fontSizeRegex } from './productIconThemeSchema.js';

const _moduleId = "vs/workbench/services/themes/common/fileIconThemeSchema";
const schemaId = 'vscode://schemas/icon-theme';
const schema = {
    type: 'object',
    allowComments: true,
    allowTrailingCommas: true,
    definitions: {
        folderExpanded: {
            type: 'string',
            description: ( localizeWithPath(
                _moduleId,
                0,
                'The folder icon for expanded folders. The expanded folder icon is optional. If not set, the icon defined for folder will be shown.'
            ))
        },
        folder: {
            type: 'string',
            description: ( localizeWithPath(
                _moduleId,
                1,
                'The folder icon for collapsed folders, and if folderExpanded is not set, also for expanded folders.'
            ))
        },
        file: {
            type: 'string',
            description: ( localizeWithPath(
                _moduleId,
                2,
                'The default file icon, shown for all files that don\'t match any extension, filename or language id.'
            ))
        },
        rootFolder: {
            type: 'string',
            description: ( localizeWithPath(
                _moduleId,
                3,
                'The folder icon for collapsed root folders, and if rootFolderExpanded is not set, also for expanded root folders.'
            ))
        },
        rootFolderExpanded: {
            type: 'string',
            description: ( localizeWithPath(
                _moduleId,
                4,
                'The folder icon for expanded root folders. The expanded root folder icon is optional. If not set, the icon defined for root folder will be shown.'
            ))
        },
        rootFolderNames: {
            type: 'object',
            description: ( localizeWithPath(
                _moduleId,
                5,
                'Associates root folder names to icons. The object key is the root folder name. No patterns or wildcards are allowed. Root folder name matching is case insensitive.'
            )),
            additionalProperties: {
                type: 'string',
                description: ( localizeWithPath(_moduleId, 6, 'The ID of the icon definition for the association.'))
            }
        },
        rootFolderNamesExpanded: {
            type: 'object',
            description: ( localizeWithPath(
                _moduleId,
                7,
                'Associates root folder names to icons for expanded root folders. The object key is the root folder name. No patterns or wildcards are allowed. Root folder name matching is case insensitive.'
            )),
            additionalProperties: {
                type: 'string',
                description: ( localizeWithPath(_moduleId, 8, 'The ID of the icon definition for the association.'))
            }
        },
        folderNames: {
            type: 'object',
            description: ( localizeWithPath(
                _moduleId,
                9,
                'Associates folder names to icons. The object key is the folder name, not including any path segments. No patterns or wildcards are allowed. Folder name matching is case insensitive.'
            )),
            additionalProperties: {
                type: 'string',
                description: ( localizeWithPath(_moduleId, 6, 'The ID of the icon definition for the association.'))
            }
        },
        folderNamesExpanded: {
            type: 'object',
            description: ( localizeWithPath(
                _moduleId,
                10,
                'Associates folder names to icons for expanded folders. The object key is the folder name, not including any path segments. No patterns or wildcards are allowed. Folder name matching is case insensitive.'
            )),
            additionalProperties: {
                type: 'string',
                description: ( localizeWithPath(_moduleId, 11, 'The ID of the icon definition for the association.'))
            }
        },
        fileExtensions: {
            type: 'object',
            description: ( localizeWithPath(
                _moduleId,
                12,
                'Associates file extensions to icons. The object key is the file extension name. The extension name is the last segment of a file name after the last dot (not including the dot). Extensions are compared case insensitive.'
            )),
            additionalProperties: {
                type: 'string',
                description: ( localizeWithPath(_moduleId, 13, 'The ID of the icon definition for the association.'))
            }
        },
        fileNames: {
            type: 'object',
            description: ( localizeWithPath(
                _moduleId,
                14,
                'Associates file names to icons. The object key is the full file name, but not including any path segments. File name can include dots and a possible file extension. No patterns or wildcards are allowed. File name matching is case insensitive.'
            )),
            additionalProperties: {
                type: 'string',
                description: ( localizeWithPath(_moduleId, 15, 'The ID of the icon definition for the association.'))
            }
        },
        languageIds: {
            type: 'object',
            description: ( localizeWithPath(
                _moduleId,
                16,
                'Associates languages to icons. The object key is the language id as defined in the language contribution point.'
            )),
            additionalProperties: {
                type: 'string',
                description: ( localizeWithPath(_moduleId, 17, 'The ID of the icon definition for the association.'))
            }
        },
        associations: {
            type: 'object',
            properties: {
                folderExpanded: {
                    $ref: '#/definitions/folderExpanded'
                },
                folder: {
                    $ref: '#/definitions/folder'
                },
                file: {
                    $ref: '#/definitions/file'
                },
                folderNames: {
                    $ref: '#/definitions/folderNames'
                },
                folderNamesExpanded: {
                    $ref: '#/definitions/folderNamesExpanded'
                },
                rootFolder: {
                    $ref: '#/definitions/rootFolder'
                },
                rootFolderExpanded: {
                    $ref: '#/definitions/rootFolderExpanded'
                },
                rootFolderNames: {
                    $ref: '#/definitions/rootFolderNames'
                },
                rootFolderNamesExpanded: {
                    $ref: '#/definitions/rootFolderNamesExpanded'
                },
                fileExtensions: {
                    $ref: '#/definitions/fileExtensions'
                },
                fileNames: {
                    $ref: '#/definitions/fileNames'
                },
                languageIds: {
                    $ref: '#/definitions/languageIds'
                }
            }
        }
    },
    properties: {
        fonts: {
            type: 'array',
            description: ( localizeWithPath(_moduleId, 18, 'Fonts that are used in the icon definitions.')),
            items: {
                type: 'object',
                properties: {
                    id: {
                        type: 'string',
                        description: ( localizeWithPath(_moduleId, 19, 'The ID of the font.')),
                        pattern: fontIdRegex,
                        patternErrorMessage: ( localizeWithPath(
                            _moduleId,
                            20,
                            'The ID must only contain letter, numbers, underscore and minus.'
                        ))
                    },
                    src: {
                        type: 'array',
                        description: ( localizeWithPath(_moduleId, 21, 'The location of the font.')),
                        items: {
                            type: 'object',
                            properties: {
                                path: {
                                    type: 'string',
                                    description: ( localizeWithPath(
                                        _moduleId,
                                        22,
                                        'The font path, relative to the current file icon theme file.'
                                    )),
                                },
                                format: {
                                    type: 'string',
                                    description: ( localizeWithPath(_moduleId, 23, 'The format of the font.')),
                                    enum: ['woff', 'woff2', 'truetype', 'opentype', 'embedded-opentype', 'svg']
                                }
                            },
                            required: [
                                'path',
                                'format'
                            ]
                        }
                    },
                    weight: {
                        type: 'string',
                        description: ( localizeWithPath(
                            _moduleId,
                            24,
                            'The weight of the font. See https://developer.mozilla.org/en-US/docs/Web/CSS/font-weight for valid values.'
                        )),
                        pattern: fontWeightRegex
                    },
                    style: {
                        type: 'string',
                        description: ( localizeWithPath(
                            _moduleId,
                            25,
                            'The style of the font. See https://developer.mozilla.org/en-US/docs/Web/CSS/font-style for valid values.'
                        )),
                        pattern: fontStyleRegex
                    },
                    size: {
                        type: 'string',
                        description: ( localizeWithPath(
                            _moduleId,
                            26,
                            'The default size of the font. See https://developer.mozilla.org/en-US/docs/Web/CSS/font-size for valid values.'
                        )),
                        pattern: fontSizeRegex
                    }
                },
                required: [
                    'id',
                    'src'
                ]
            }
        },
        iconDefinitions: {
            type: 'object',
            description: ( localizeWithPath(
                _moduleId,
                27,
                'Description of all icons that can be used when associating files to icons.'
            )),
            additionalProperties: {
                type: 'object',
                description: ( localizeWithPath(
                    _moduleId,
                    28,
                    'An icon definition. The object key is the ID of the definition.'
                )),
                properties: {
                    iconPath: {
                        type: 'string',
                        description: ( localizeWithPath(
                            _moduleId,
                            29,
                            'When using a SVG or PNG: The path to the image. The path is relative to the icon set file.'
                        ))
                    },
                    fontCharacter: {
                        type: 'string',
                        description: ( localizeWithPath(
                            _moduleId,
                            30,
                            'When using a glyph font: The character in the font to use.'
                        ))
                    },
                    fontColor: {
                        type: 'string',
                        format: 'color-hex',
                        description: ( localizeWithPath(_moduleId, 31, 'When using a glyph font: The color to use.'))
                    },
                    fontSize: {
                        type: 'string',
                        description: ( localizeWithPath(
                            _moduleId,
                            32,
                            'When using a font: The font size in percentage to the text font. If not set, defaults to the size in the font definition.'
                        )),
                        pattern: fontSizeRegex
                    },
                    fontId: {
                        type: 'string',
                        description: ( localizeWithPath(
                            _moduleId,
                            33,
                            'When using a font: The id of the font. If not set, defaults to the first font definition.'
                        ))
                    }
                }
            }
        },
        folderExpanded: {
            $ref: '#/definitions/folderExpanded'
        },
        folder: {
            $ref: '#/definitions/folder'
        },
        file: {
            $ref: '#/definitions/file'
        },
        folderNames: {
            $ref: '#/definitions/folderNames'
        },
        folderNamesExpanded: {
            $ref: '#/definitions/folderNamesExpanded'
        },
        rootFolder: {
            $ref: '#/definitions/rootFolder'
        },
        rootFolderExpanded: {
            $ref: '#/definitions/rootFolderExpanded'
        },
        rootFolderNames: {
            $ref: '#/definitions/rootFolderNames'
        },
        rootFolderNamesExpanded: {
            $ref: '#/definitions/rootFolderNamesExpanded'
        },
        fileExtensions: {
            $ref: '#/definitions/fileExtensions'
        },
        fileNames: {
            $ref: '#/definitions/fileNames'
        },
        languageIds: {
            $ref: '#/definitions/languageIds'
        },
        light: {
            $ref: '#/definitions/associations',
            description: ( localizeWithPath(
                _moduleId,
                34,
                'Optional associations for file icons in light color themes.'
            ))
        },
        highContrast: {
            $ref: '#/definitions/associations',
            description: ( localizeWithPath(
                _moduleId,
                35,
                'Optional associations for file icons in high contrast color themes.'
            ))
        },
        hidesExplorerArrows: {
            type: 'boolean',
            description: ( localizeWithPath(
                _moduleId,
                36,
                'Configures whether the file explorer\'s arrows should be hidden when this theme is active.'
            ))
        },
        showLanguageModeIcons: {
            type: 'boolean',
            description: ( localizeWithPath(
                _moduleId,
                37,
                'Configures whether the default language icons should be used if the theme does not define an icon for a language.'
            ))
        }
    }
};
function registerFileIconThemeSchemas() {
    const schemaRegistry = ( (Registry.as(Extensions.JSONContribution)));
    schemaRegistry.registerSchema(schemaId, schema);
}

export { registerFileIconThemeSchemas };
